import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";

const DESTS = [
  { label: "USA", match: "USA", flag: "🇺🇸" },
  { label: "UK", match: "United Kingdom", flag: "🇬🇧" },
  { label: "Canada", match: "Canada", flag: "🇨🇦" },
  { label: "Australia", match: "Australia", flag: "🇦🇺" },
  { label: "New Zealand", match: "New Zealand", flag: "🇳🇿" },
  { label: "Europe", match: "Germany", flag: "🇪🇺" },
  { label: "UAE & Middle East", match: "UAE", flag: "🇦🇪" },
  { label: "Asia-Pacific", match: "Singapore", flag: "🇸🇬" },
  { label: "Africa", match: "South Africa", flag: "🇿🇦" },
  { label: "Rest of World", match: null, flag: "🌐" },
];

export default function Destinations() {
  const [cards, setCards] = useState([]);
  const [query, setQuery] = useState("");
  useEffect(() => {
    api.get("/rates/countries").then((r) => setCards(r.data.cards || [])).catch(() => {});
  }, []);
  const cardFor = (m) => cards.find((c) => c.country === m);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return cards.filter((c) => c.country.toLowerCase().includes(q)).slice(0, 12);
  }, [cards, query]);

  return (
    <section id="destinations" className="scroll-mt-24 bg-white py-20 sm:py-28" data-testid="global-destinations">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
          <span className="h-2 w-2 rounded-full bg-saffron" /> Global destinations
        </p>
        <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-navy sm:text-5xl">
          From India. <span className="text-saffron">To the world.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-500 sm:text-base">
          Lowest possible live rates across all courier partners — 21kg+ bulk per-kg pricing shown where available.
          Pick a destination to get an instant quote.
        </p>

        <div className="relative mt-8 max-w-xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search any country to see its lowest price…"
            data-testid="destination-country-search"
            className="w-full rounded-full border border-stone-200 bg-stone-50 py-3.5 pl-12 pr-4 text-sm font-medium text-navy shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-saffron/60 focus:bg-white focus:ring-2 focus:ring-saffron/20"
          />
        </div>

        {query.trim() ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="destination-search-results">
            {matches.length ? matches.map((c, i) => (
              <Link key={c.country}
                    to={`/rate-calculator?mode=international&country=${encodeURIComponent(c.country)}`}
                    data-testid={`destination-search-item-${i}`}
                    className="group flex items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-stone-50 px-5 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-saffron/50 hover:bg-white hover:shadow-lg hover:shadow-stone-200">
                <div className="min-w-0">
                  <h3 className="truncate font-heading text-base font-semibold text-navy">{c.country}</h3>
                  {c.transit_days ? <p className="mt-0.5 text-xs font-semibold text-slate-400">{c.transit_days}</p> : null}
                </div>
                <div className="shrink-0 text-right">
                  {c.per_kg_21 ? (
                    <p data-testid={`destination-search-price-${i}`}>
                      <span className="font-heading text-xl font-extrabold text-saffron">₹{Math.round(c.per_kg_21).toLocaleString("en-IN")}</span>
                      <span className="block text-[11px] font-semibold text-slate-500">/kg · 21kg+ bulk</span>
                    </p>
                  ) : (
                    <p data-testid={`destination-search-price-${i}`} className="text-xs font-semibold text-slate-500">
                      From <span className="font-heading text-base font-extrabold text-saffron">₹{Math.round(c.from).toLocaleString("en-IN")}</span>
                    </p>
                  )}
                </div>
              </Link>
            )) : (
              <p className="col-span-full rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-5 py-6 text-sm font-medium text-slate-500"
                 data-testid="destination-search-empty">
                No country found for “{query.trim()}”. Try another spelling, or request a custom quote.
              </p>
            )}
          </div>
        ) : null}

        <div className="mt-12 grid gap-4 lg:grid-cols-2">
          {DESTS.map((d, i) => {
            const card = cardFor(d.match);
            return (
              <Link key={d.label}
                    to={`/rate-calculator?mode=international${d.match ? `&country=${encodeURIComponent(d.match)}` : ""}`}
                    data-testid={`destination-card-${i}`}
                    className="group flex items-center gap-5 rounded-2xl border border-stone-200 bg-stone-50 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-saffron/50 hover:bg-white hover:shadow-xl hover:shadow-stone-200 sm:p-6">
                <span className="shrink-0 text-4xl leading-none sm:text-5xl" role="img" aria-label={`${d.label} flag`}>{d.flag}</span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg font-semibold text-navy sm:text-xl">{d.label}</h3>
                  {card ? (
                    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid={`destination-price-${i}`}>
                      {card.per_kg_21 ? (
                        <p className="text-xs font-semibold text-slate-500">
                          <span className="font-heading text-xl font-extrabold text-saffron sm:text-2xl">₹{Math.round(card.per_kg_21).toLocaleString("en-IN")}/kg</span>
                          {" "}21kg+ bulk
                        </p>
                      ) : null}
                      <p className="text-xs font-semibold text-slate-400">
                        From ₹{Math.round(card.from).toLocaleString("en-IN")}{card.transit_days ? ` · ${card.transit_days}` : ""}
                      </p>
                    </div>
                  ) : (
                    <p className="mt-1.5 text-xs font-semibold text-slate-400">Custom quote on request</p>
                  )}
                </div>
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-saffron/10 text-saffron transition-all group-hover:bg-saffron group-hover:text-white">
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
